// Namespaces per docs/rules/i18n.md; only `common.*` is deduped.
export default {
  common: {
    apply: 'Apply',
    back: 'Back',
    brands: 'Brands',
    cancel: 'Cancel',
    continue: 'Continue',
    delete: 'Delete',
    goBack: 'Go Back',
    learnMore: 'Learn More',
    logIn: 'Log In',
    logOut: 'Log Out',
    next: 'Next',
    nextItem: 'Next item',
    optional: '(optional)',
    other: 'Other',
    previous: 'Previous',
    previousItem: 'Previous item',
    resetAll: 'Reset all',
    retry: 'Retry',
    save: 'Save',
    seeMore: 'See More',
    tags: 'Tags',
    tip: 'Tip',
    tryAgain: 'Try again',
    unscheduled: 'Unscheduled',
  },
  ui: {
    noConnectionPlaceholder: {
      title: '{channel} Analytics',
      sectionTitle: 'Connect your {channel} account',
      connect: 'Connect {channel}',
    },
    scopedBrandBanner: {
      title: 'You’re working in {brandName}',
      message:
        'Changes made here apply to this brand. Your selected brand is still {selectedBrandName}.',
    },
  },
  app: {
    adContentDrawer: {
      title: 'Ad content',
      loadError: 'Unable to load ad content.',
      loadMoreError: 'Unable to load more ad content.',
    },
    dashboards: {
      metrics: {
        groupedRankingDisabled: 'This metric is not available with the selected Content Level.',
        placementUnavailable:
          'Non-additive metrics are unavailable while a Placement filter is active.',
        unavailable: 'This metric is unavailable for the current report.',
      },
      sorting: {
        invalidSavedSort:
          'This saved sort is invalid. {reason} Choose an available metric and sort order.',
      },
    },
    adInsightsDrawer: {
      adTitle: 'Ad Insights',
      adNameTitle: 'Ad Name Insights',
      creativeTitle: 'Creative Insights',
      openDetails: 'Open insights for {name}',
      unavailableTitle: 'Ad insights unavailable',
      unavailableMessage: 'This content could not be found or you do not have access to it.',
      invalidLink: 'This ad insights link is invalid or no longer supported.',
      disabledMessage: 'Ad insights are not available for this account.',
      errorTitle: 'Something went wrong.',
      errorMessage: 'Unable to load ad insights. Please try again.',
      previewLabel: 'Ad preview',
      actionsLabel: 'Ad actions',
      sectionsLabel: 'Ad insights',
      loadingLabel: 'Loading ad insights',
      loadingMessage: 'Loading ad insights…',
    },
    analytics: {
      channelLayout: {
        navigationTitle: 'Analytics',
      },
      channelNoAccessBanner: {
        noBrandPermission: "Looks like this feature isn't included in your plan!",
        noUserPermission: "Looks like you don't have permission to use this feature!",
        noAccessMessage:
          "Dash Social brings you all of the insights you love, including {channel}! Ask your Dash Social administrator to provide you access. Or if this feature isn't in your package, contact your Customer Success Representative to try it out!",
      },
    },
    brandGovernance: {
      navLabel: 'Governance',
      header: {
        eyebrow: 'Vision knows your content, sentiment, performance, and more',
        title: 'Now give it {accent}',
        titleAccent: 'your brand rules',
        subtitle: 'Each rule you add shows Vision what on-brand looks like for you.',
      },
      status: {
        notSetUp: 'Not set up',
        usedByVision: 'Used by Vision',
        optional: 'Optional',
      },
      cards: {
        brandGuidelines: {
          title: 'Brand guidelines',
          description:
            'Upload your brand fonts and logos to check your content and create new content that matches.',
          addCta: 'Add brand guidelines',
          editCta: 'Edit brand guidelines',
        },
        brandSafety: {
          title: 'Brand safety',
          description:
            "List anything your brand avoids, like competitors, alcohol, or certain keywords, and we'll flag content that includes it.",
          addCta: 'Add exclusions',
          editCta: 'Edit exclusions',
        },
        otherRules: {
          title: 'Other rules',
          description:
            "Anything else we should know? Add rules or preferences that aren't already covered, and we'll follow them across the platform.",
          addCta: 'Add rules',
          editCta: 'Edit rules',
        },
      },
    },
    campaigns: {
      adContentReport: {
        title: 'Ads',
        tooltip:
          "Shows performance for the Meta ad campaigns you've selected, based on your reporting period and filters",
      },
      reviewRecipients: {
        addMissingEmailsTitle: 'Add Missing Emails',
        recipientsTitle: 'Recipients',
      },
      emailComposer: {
        title: 'Compose Email',
        send: 'Send to one Creator | Send to {count} Creators',
        loadingRecipients: 'Loading recipients',
        recipients: 'Recipients ({count})',
        creator: 'Creator',
        emailAddress: 'Email Address',
        recipientLoadErrorTitle: "Recipients couldn't be loaded",
        recipientLoadErrorMessage: 'Try loading the selected creators again.',
        tryAgain: 'Try Again',
        missingInformation:
          'One creator is missing information used in this email | {count} creators are missing information used in this email',
        sendError: "We couldn't send this email. Your draft is saved; please try again.",
        cc: 'CC',
        bcc: 'BCC',
        addEmailAddresses: 'Add email addresses',
        subject: 'Subject',
        enterSubject: 'Enter subject',
        subjectRequired: 'Subject is required',
        message: 'Message',
        enterMessage: 'Enter message',
        messageRequired: 'Message is required',
        insertMessageVariable: 'Insert message variable',
        insertDynamicLink: 'Insert dynamic link',
        discardTitle: 'Discard email?',
        discardMessage: 'If you leave now, your unsent draft will be lost.',
        keepEditing: 'Keep editing',
        discard: 'Discard',
        variables: {
          firstName: 'First name',
          lastName: 'Last name',
          email: 'Email',
          phone: 'Phone number',
          address: 'Address',
          instagramHandle: 'Instagram handle',
          tiktokHandle: 'TikTok handle',
          youtubeHandle: 'YouTube handle',
          campaignName: 'Campaign name',
        },
        links: {
          instagramConnection: 'Instagram Creator Connection',
          tiktokConnection: 'TikTok Creator Connection',
          informationRequest: 'Information Request Form',
        },
      },
    },
    competitive: {
      teaser: {
        heading: 'How do you stack up against the competition?',
        subheading: 'We added industry-leading Instagram competitors to get you started.',
        addCompetitors: 'Add Competitors',
      },
    },
    creators: {
      payments: {
        taxWithholdingBanner: {
          title: 'Tax Withholding Applies',
          message:
            "The creator's tax forms trigger U.S. backup withholding. Your brand keeps the withheld amount to report and remit.",
          learnMore: 'Learn more',
        },
        paymentHistory: {
          taxWithheldTooltip: 'Taxes were withheld from this payment. Click to view details.',
        },
        sendPayment: {
          withholdingHint: {
            withholding: 'Tax Withholding:',
            percentage: '({percentage})',
            creatorReceives: 'Creator Receives:',
            beforeFees: '(before fees)',
            fxRate: "{'|'} FX Rate: {rate}",
          },
        },
      },
    },
    instagram: {
      filters: {
        crossposted: 'Cross posted',
      },
      hashtagMonitor: {
        noUserAccessTitle: "You Don't Have Access to Hashtag Monitoring",
      },
    },
    auth: {
      businessEmail: 'Business Email',
      password: 'Password',
      mustStartWithLetter: 'Must start with a letter',
      logInWithSso: 'Log In With SSO',
      login: {
        forgotPassword: 'Forgot Password',
      },
      accountSelector: {
        pageId: 'Page ID: {id}',
      },
      referralBanner: {
        title: 'Refer Dash and earn $250.',
      },
      accountSetup: {
        welcome: 'Welcome to {appName}',
        intro:
          "We're excited for you to begin collaborating with your team! As a first step, please confirm the following basic information.",
        firstName: 'First Name',
        firstNamePlaceholder: 'Enter first name',
        lastName: 'Last Name',
        lastNamePlaceholder: 'Enter last name',
        jobTitle: 'Job Title',
        jobTitlePlaceholder: 'Enter job title',
        timezone: 'Timezone',
        timezonePlaceholder: 'Search for time zone',
      },
      connect: {
        thankYou: 'Thank You!',
        bluesky: {
          title: 'Connect Your Bluesky Account',
          success: 'Your Bluesky account has been connected to Dash Social.',
        },
        facebook: {
          title: 'Connect Your Instagram Account for',
          success: 'Your Facebook account has been connected to Dash Social.',
        },
        facebookAds: {
          title: 'Connect Your Facebook Ads Accounts for',
          success: 'Your Facebook Ads accounts have been connected to Dash Social.',
        },
        pinterest: {
          title: 'Connect Your Pinterest Account for',
          success:
            'Your account has been connected to Dash Social. We are now analyzing your Pinterest data.',
          featuredArticle: 'Featured article:',
          articleTitleLine1: 'Growing Your Organic',
          articleTitleLine2: 'Pinterest Strategy',
          blogCaption: 'Learn more about visual marketing on {blog}!',
          blog: 'our blog',
        },
        snapchat: {
          title: 'Connect Your Snapchat Account',
          success: 'Your Snapchat account has been connected to Dash Social.',
        },
        threads: {
          title: 'Connect Your Threads Profile',
          success: 'Your Threads profile has been connected to Dash Social.',
        },
        tiktok: {
          title: 'Connect Your TikTok Account for',
          success: 'Your TikTok account has been connected to Dash Social.',
        },
        tiktokAds: {
          title: 'Connect Your TikTok Ads Account for',
          success: 'Your TikTok Ads account has been connected to Dash Social.',
        },
        twitter: {
          title: 'Connect Your X Account for',
          success: 'Your X account has been connected to Dash Social.',
        },
        youtube: {
          title: 'Connect Your YouTube Channel',
          success: 'Your YouTube channel has been connected to Dash Social.',
        },
      },
      creatorConnect: {
        tryAgain: 'Try Again',
        acceptAndConnect: 'Accept & Connect',
        switchIcon: 'switch-horizontal icon',
      },
      forgotPassword: {
        info: 'Please enter your email address to reset the password.',
        reset: 'Reset Password',
      },
      oauthConsent: {
        expiredTitle: 'This Link Has Expired',
        expiredMessage: 'Return to the app you started from and try connecting Dash Social again.',
        logo: 'Dash Social',
        title: 'Connect with Dash Social MCP',
        grant: 'Grant {client} access to Dash Social',
        connectingAs: 'Connecting as:',
        scopes: 'Dash Social MCP will be able to:',
        agreement: 'By continuing, you confirm that you agree to Dash Social’s {privacyPolicy}.',
        privacyPolicy: 'Privacy Policy',
        questions: 'Have questions? {learnMore}',
      },
      onboarding: {
        stepCounter: 'Step {step} of {total}',
        welcome: 'welcome to {appName}',
        getToKnowYou: "Let's get to know you.",
        connectChannels: 'Connect Your Channels',
        connectChannelsHelp:
          'To set up your {appName} account, you will need to connect at least one social channel.',
        notReadyToConnect: 'Not ready to connect?',
        inviteHelp:
          'Additional users are always free with {appName}. Who do you want to collaborate with?',
        skip: 'I’ll do this later',
      },
      otp: {
        scanTitle: 'Scan the QR code',
        scanMessage:
          'MFA (multi-factor authentication) is required to complete your signup. Scan the QR code with your authenticator app.',
        secureTitle: 'Secure Your Account',
        codeLabel: 'Verification Code (6-digit)',
      },
      passwordReset: {
        title: 'Set Your Password',
      },
      setPassword: {
        trustedBy: 'TRUSTED BY TEAMS AT:',
        completeSignup: 'Complete Signup',
        signUpWithGoogle: 'Sign Up With Google',
      },
      ssoLogin: {
        info: 'Please enter the email address you use for your organization.',
      },
      ssoLoginError: {
        imageTitle: 'Page not found',
        title: "Looks like there's a problem with your account",
        contact: '{reason} Please contact us for help!',
        home: 'Take me home',
      },
    },
    landingPage: {
      title: 'Social Entertainment Overview',
      reportingPeriod: 'Reporting Period',
      reportingPeriodTooltip:
        'Activity on posts published or mentions received within the reporting period',
    },
    mediaInsightsDrawer: {
      loadingTitle: 'Loading insights title',
      previousMedia: 'Previous media',
      nextMedia: 'Next media',
      loadingLabel: 'Loading media',
      loadingMessage: 'Loading media…',
      metricView: 'Metric view',
      mediaDetails: 'Media details',
      postInformation: {
        instagramUser: 'Instagram User',
      },
      twitterPreview: {
        loading: 'Loading preview…',
      },
    },
    scheduler: {
      postEditor: {
        postUnavailable: 'Unable to load post.',
      },
    },
    primaryNavigation: {
      analytics: 'Analytics',
      campaigns: 'Campaigns',
      community: 'Community',
      competitors: 'Competitors',
      constellationPro: 'Constellation Pro',
      creators: 'Creators',
      dashboards: 'Dashboards',
      homeLink: 'Go to the Dash Social home page',
      library: 'Library',
      listening: 'Listening',
      scheduler: 'Scheduler',
      visionHome: 'Vision Home',
    },
    visionHub: {
      actionsNeeded: {
        title: 'Actions Needed',
        seeAll: 'See All',
        loadError: 'Could not load Actions Needed.',
        retry: 'Retry',
        noMatches: 'No actions match your filters.',
        pagination: 'Actions Needed pages',
        taskFilter: 'Task',
        statusFilter: 'Status',
        columns: {
          task: 'Task',
          brand: 'Brand',
          lastRun: 'Last Run',
          action: 'Action',
        },
        unread: 'Unread',
        review: 'Review',
        reviewLabel: 'Review {task}',
      },
      taskActivity: {
        loadError: 'Could not load activity.',
        retry: 'Retry',
        totals: 'Activity totals',
        emptyTitle: 'No Runs Yet',
        emptyMessage:
          'Click the “Run Task” button at the top to start your task and check that the instructions are working correctly',
        columns: {
          runs: 'Runs',
          action: 'Action',
          date: 'Date',
          status: 'Status',
        },
        unread: 'Unread',
        pagination: 'Run pages',
      },
      taskRun: {
        placeholder: 'How can I help you today?',
        visionHub: 'Vision Hub',
        newChat: 'New chat',
        moreOptions: 'More options',
        approve: 'Approve',
        reject: 'Reject',
        decisionError: 'Could not save your decision. Try again.',
      },
      lastRunStatus: {
        column: 'Last Run Status',
        labels: {
          succeeded: 'Successful',
          failed: 'Failed',
          needsApproval: 'Needs Your Approval',
          succeededWithWarnings: 'Succeeded with Warnings',
        },
        tooltips: {
          failed: 'A task failed and needs review',
          needsApproval: 'A task needs approval',
          succeededWithWarnings: 'A task completed with warnings',
        },
      },
      taskSharing: {
        title: 'Share with Team',
        share: 'Share',
        searchUsers: 'Search Users',
        searchBrands: 'Search Brands',
        users: 'Users',
        brands: 'Brands',
        user: 'User',
        email: 'Email',
        access: 'Access',
        changeAccess: 'Change Access',
        userAccess: 'User access',
        clearSelection: 'Clear Selection',
        selectAllUsers: 'Select all users',
        selectAllBrands: 'Select all brands',
        brand: 'Brand',
        brandUserCount: '0 users | 1 user | {count} users',
        add: 'Add',
        viewer: 'Viewer',
        editor: 'Editor',
        removeAccess: 'Remove Access',
        userRoleUpdated: "{name}'s role has been updated on {taskName}.",
        userAccessAdded: '{name} has been added to {taskName}.',
        userAccessRemoved: "{name}'s access has been removed from {taskName}.",
        bulkUserAccessUpdated: 'Access updated for {count} users on {taskName}',
        bulkUserAccessRemoved: 'Access removed for {count} users on {taskName}.',
        brandAccessAdded: 'Access added for {name} on {taskName}.',
        brandAccessUpdated: 'Access updated for {name} on {taskName}.',
        brandAccessRemoved: 'Access removed for {name} on {taskName}.',
        bulkBrandAccessUpdated: 'Access updated for {count} brands on {taskName}.',
        bulkBrandAccessRemoved: 'Access removed for {count} brands on {taskName}.',
        selectedCount: 'No items selected | 1 item selected | {count} items selected',
        noUsers: 'No users found',
        noBrands: 'No brands configured',
        noBrandsFound: 'No brands found',
        loadMore: 'Load more',
        retry: 'Retry',
        loading: 'Loading sharing…',
        loadFailed: 'Could not load sharing.',
        usersFailed: 'Could not load eligible users.',
        saveFailed: 'Could not save access. Review selections and retry.',
        accessUnavailable: 'You no longer have access to manage sharing for this task.',
        unknownUser: 'User #{id}',
      },
      scheduledTasks: {
        resolveHighlightedErrors: 'Resolve the highlighted errors before saving.',
        selectTime: 'Select Time',
        monthlyPicker: {
          startsOn: 'Starts on',
        },
        scheduleSelect: {
          placeholder: 'Choose how often you want the task to run',
        },
        settingsForm: {
          nameLabel: 'Task Name*',
          namePlaceholder: 'Enter task name',
          descriptionLabel: 'Description',
          descriptionPlaceholder: 'Describe what the task does for you',
          brandsLabel: 'Brands*',
          brandsPlaceholder: 'Select the brands you want the task to run for',
          brandsSearchPlaceholder: 'Search Brands',
          brandsHeader: 'Your Brands',
          scheduleLabel: 'Scheduled*',
          instructionsLabel: 'Task Instructions*',
          instructionsPlaceholder: 'Write custom instructions',
          approvalLockedLabel: 'Approval requirement cannot be changed',
          approvalLabel: 'Approval Required',
          approvalTooltip:
            'If approval is required, the agent will always check with you before running.',
        },
      },
    },
    settings: {
      tagCategories: {
        community: 'Community',
        title: 'Tag Categories',
        sortCategory: 'Tag Category',
        edit: 'Edit Tag Category',
        membership: 'Tags in {name} ({brand})',
        actions: 'Actions for {name} ({brand})',
        searchLabel: 'Search tag categories',
        searchPlaceholder: 'Search category names',
        categoriesError: 'Tag categories could not be loaded.',
        tagsError: 'Tags could not be loaded.',
        emptyTitle: 'Measure What Matters',
        emptyMessage:
          'Group related Tags into Categories to understand conversation themes across your brands, track trends, and uncover which themes drive engagement.',
        suggestCategories: 'Suggest Categories',
        noResultsTitle: 'No Results Found',
        noResultsMessage: 'Try different keywords, checking for typos, or adjusting your filters',
        loading: 'Loading tag categories',
        pagination: 'Tag categories pagination',
      },
      community: {
        sentimentAndSpam: {
          edit: 'Edit sentiment and spam',
          resetToDefault: 'Reset to default',
          definitionFor: {
            POSITIVE: 'Positive definition for {brand}',
            NEGATIVE: 'Negative definition for {brand}',
            SPAM: 'Spam definition for {brand}',
          },
          resetDefinitionFor: {
            POSITIVE: 'Reset Positive to default for {brand}',
            NEGATIVE: 'Reset Negative to default for {brand}',
            SPAM: 'Reset Spam to default for {brand}',
          },
        },
      },
      communityTags: {
        searchLabel: 'Search tags',
        searchPlaceholder: 'Search tag names',
        filters: {
          categories: 'Tag Categories',
          searchBrands: 'Search brands',
          searchCategories: 'Search tag categories',
          uncategorized: 'Uncategorized',
          on: 'On',
          off: 'Off',
        },
        columns: {
          tags: 'Tags',
          category: 'Tag Category',
          brands: 'Brands',
          description: 'Tag Description',
          autoTagging: 'Auto-Tagging',
        },
        tooltips: {
          category:
            'Categories group related Tags into high-level themes, making it easier to measure and understand what your community is talking about.',
          description:
            'Descriptions define what types of messages should receive a Tag. Vision uses the description to identify and automatically tag matching messages.',
          autoTagging:
            'Auto-tagging automatically applies Tags to new messages based on their definitions',
          usedInTask:
            'Auto-tagging can’t be disabled because this tag is being used in an Agent Task. Remove the tag from the task or delete the task first.',
          missingDescription:
            'Auto-tagging requires a tag description to identify matching messages.',
        },
        autoTaggingLabel: 'Auto-tagging for {name} ({brand})',
        autoTaggingUpdateError: 'Auto-tagging could not be updated. Please try again.',
        taskLink: 'See in Agent Task | See in {count} Agent Tasks',
        taskLinkLabel: 'See in Agent Task: {name}',
        loadError: 'Tags could not be loaded.',
        categoriesLoadError: 'Tag categories could not be loaded.',
        tasksLoadError: 'Agent Tasks could not be loaded.',
        noTags: 'No tags found.',
        visionTitle: 'Try Vision',
        visionMessage:
          'Prioritize every message that hits your inbox. Let Vision evaluate your current Tags and suggest Categories and Tags that reflect what your community is saying.',
        suggestCategories: 'Suggest Tag Categories',
        autoTaggingExplanation:
          'Tags will be automatically applied to messages in Community when auto-tagging is enabled. Vision will match messages based on the description provided. Tags can also be applied by an existing Agent Task.',
        loading: 'Loading tags',
        paginationLabel: 'Tags pagination',
        showing: 'Showing {start}–{end} tags',
      },
      contentTagPicker: {
        upsell: 'Upgrade to the Enterprise Plan to use exclusive content tagging',
        searchPlaceholder: 'Search tags',
        moreCount: '+{count} more',
      },
      privacyNotice: {
        title: 'Privacy Notice',
        message:
          "Dash Social's Web Tracker records page URLs and page views to measure content performance and does not store information identifying individual website visitors.",
      },
      platformConnections: {
        filters: {
          googleAnalyticsTooltip:
            'Connect your Google Analytics account to see website traffic, transactions, and revenue in Campaigns',
        },
        bulkDisconnect: {
          warning:
            "Insights and content will no longer be imported into Dash Social and you won't be able to publish any posts.",
        },
      },
    },
  },
  metrics: {},
  errors: {},
  labels: {},
} as const;
